// PRD.299 — WhatsApp "appointment booked" confirmation: clinic default and the
// per-booking toggle state.
//
// The backend is the authority on whether a message may be sent (it re-checks
// source, opt-in, template and phone). This module only decides what the
// booking form should SHOW, so staff are never offered a toggle that the
// backend would silently skip.
//
// Self-contained (no workspace imports) so it runs under the node:test harness.
// The `app_config` row is accepted via a structural shape that AppConfigRow
// satisfies.

/** Booking provenance that opts into APPOINTMENT_BOOKED. */
export const WEB_ADMIN_APPOINTMENT_SOURCE = 'WEB_ADMIN';

/**
 * `notification_jobs.client_reference` for this notification. Mirrors the
 * existing `appointmentReminderReference()` convention.
 */
export function appointmentBookedReference(appointmentId: string): string {
  return `appointment:${appointmentId}:whatsapp-booked`;
}

/** Default when a clinic has saved no preference. */
export const APPOINTMENT_BOOKED_DEFAULT_ON = true;

export interface AppointmentBookedConfigSource {
  extra_json?: unknown;
}

function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (typeof extraJson === 'object' && !Array.isArray(extraJson)) {
    return extraJson as Record<string, unknown>;
  }
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* fall through */
    }
  }
  return {};
}

/**
 * Whether the "Send WhatsApp confirmation" toggle starts on, from
 * `app_config.extra_json.notification_settings.appointment_booked_default`.
 *
 * Missing/malformed config means ON — a clinic that never configured this
 * still gets confirmations, and only an explicit `false` turns them off.
 */
export function getAppointmentBookedDefault(
  row: AppointmentBookedConfigSource | null | undefined,
): boolean {
  const extra = parseExtraJson(row?.extra_json);
  const settings = extra.notification_settings;
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) {
    return APPOINTMENT_BOOKED_DEFAULT_ON;
  }
  const value = (settings as Record<string, unknown>).appointment_booked_default;
  return typeof value === 'boolean' ? value : APPOINTMENT_BOOKED_DEFAULT_ON;
}

/** Serialize the default back into `extra_json`, preserving every other key. */
export function writeAppointmentBookedDefault(currentExtraJson: unknown, value: boolean): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  const settings = parseExtraJson(next.notification_settings);
  settings.appointment_booked_default = value;
  next.notification_settings = settings;
  return JSON.stringify(next);
}

/* ------------------------------------------------------------------ */
/*  Toggle state                                                       */
/* ------------------------------------------------------------------ */

/** Matches `whatsAppOptInState()` in web-admin's patient-communication lib. */
export type WhatsAppOptInState = 'opted-in' | 'opted-out' | 'not-set';

export type WhatsAppToggleBlockReason = 'no_mobile_number' | 'opt_in_not_set' | 'opted_out';

export interface WhatsAppToggleState {
  /** Whether the toggle is interactive. */
  enabled: boolean;
  /** Whether it starts checked. Always false when disabled. */
  checked: boolean;
  /** Why it is disabled — drives the inline hint. */
  reason: WhatsAppToggleBlockReason | null;
  /**
   * Whether to offer an inline "enable WhatsApp messages" action. Only for
   * `not-set`: an explicit opt-OUT must not be reversed from a booking screen.
   */
  canOptInInline: boolean;
}

export interface WhatsAppToggleInput {
  optInState: WhatsAppOptInState;
  hasMobileNumber: boolean;
  clinicDefault: boolean;
}

/**
 * Resolve how the "Send WhatsApp confirmation" toggle should render.
 *
 * A missing mobile number outranks opt-in state: without a destination the
 * message cannot be sent whatever the consent says, and "No mobile number on
 * file" is the more actionable message.
 */
export function resolveWhatsAppToggleState(input: WhatsAppToggleInput): WhatsAppToggleState {
  if (!input.hasMobileNumber) {
    return { enabled: false, checked: false, reason: 'no_mobile_number', canOptInInline: false };
  }
  if (input.optInState === 'opted-out') {
    return { enabled: false, checked: false, reason: 'opted_out', canOptInInline: false };
  }
  if (input.optInState === 'not-set') {
    return { enabled: false, checked: false, reason: 'opt_in_not_set', canOptInInline: true };
  }
  return { enabled: true, checked: input.clinicDefault, reason: null, canOptInInline: false };
}

/** Human-readable hint for a blocked toggle. */
export function whatsAppToggleHint(reason: WhatsAppToggleBlockReason | null): string | null {
  switch (reason) {
    case 'no_mobile_number':
      return 'No mobile number on file';
    case 'opted_out':
      return 'Patient opted out of WhatsApp messages';
    case 'opt_in_not_set':
      return 'Patient has not agreed to WhatsApp messages yet';
    default:
      return null;
  }
}
